// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { GridColDef } from '@mui/x-data-grid-pro';
import { EmploymentDayViolationResponse } from 'generated/index';

// import utils
import { dateFormatter } from 'utils/grid';
import { Icon } from '@sdb/planning-ui';
import { IconBox } from './DayLimitViolations.styles';

export const useColumnDefinitions = ({
  showEmployeeName = false
}: {
  showEmployeeName?: boolean;
}) => {
  const { t } = useTranslation('common');

  return useMemo(
    () =>
      [
        {
          field: 'employmentNumber',
          type: 'string',
          headerName: t('dayLimitViolations.employmentNumber'),
          valueGetter: (_, row: EmploymentDayViolationResponse) =>
            row?.employment?.number ?? '',
          width: 150
        },
        {
          field: 'employeeNumber',
          type: 'string',
          headerName: t('dayLimitViolations.employeeNumber'),
          valueGetter: (_, row: EmploymentDayViolationResponse) =>
            row?.employment?.employeeNumber ?? '',
          width: 150
        },
        ...(showEmployeeName
          ? [
              {
                field: 'employeeName',
                type: 'string',
                headerName: t('employee'),
                valueGetter: (_, row: EmploymentDayViolationResponse) =>
                  row?.employment?.employeeName ?? '',
                flex: 1,
                minWidth: 150
              }
            ]
          : []),
        {
          field: 'organizationalUnitName',
          type: 'string',
          headerName: t('organizationalUnit'),
          valueGetter: (_, row: EmploymentDayViolationResponse) =>
            row?.employment?.organizationalUnitName ?? '',
          flex: 1,
          minWidth: 150
        },
        {
          field: 'periodStart',
          type: 'date',
          headerName: t('dayLimitViolations.periodStart'),
          valueFormatter: dateFormatter,
          width: 150
        },
        {
          field: 'periodEnd',
          type: 'date',
          headerName: t('dayLimitViolations.periodEnd'),
          valueFormatter: dateFormatter,
          width: 150
        },
        {
          field: 'periodDescription',
          type: 'number',
          headerName: t('dayLimitViolations.periodDescription'),
          width: 150,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'numberOfWorkingDays',
          type: 'number',
          headerName: t('dayLimitViolations.numberOfWorkingDays'),
          width: 160,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'numberOfViolationDays',
          type: 'number',
          headerName: t('dayLimitViolations.violation'),
          width: 160,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'numberOfPaidOutDays',
          type: 'number',
          headerName: t('dayLimitViolations.numberOfPaidOutDays'),
          width: 160,
          align: 'left',
          headerAlign: 'left'
        },
        {
          field: 'isUnjustifiedPayOut',
          type: 'boolean',
          headerName: t('dayLimitViolations.unjustifiedPayOut'),
          width: 130,
          renderCell: (params) =>
            params.value ? (
              <IconBox>
                <Icon
                  type="triangle-exclamation"
                  color="warning"
                  fontSize="small"
                />
              </IconBox>
            ) : null
        }
      ] as GridColDef[],
    [t]
  );
};

export default useColumnDefinitions;
